/*
 * <header-drawer> — the mobile menu.
 *
 * The drawer is a native <details>, and every group inside it opens with a
 * checkbox + label pair. That means the whole menu works with JS disabled: the
 * panel appears, groups expand, links navigate.
 *
 * This element adds only the parts that need script:
 *   * a `menu-opening` class one frame after `open`, so the panel can slide in
 *     rather than appear;
 *   * `--viewport-height` and `--header-bottom-position`, so the panel is
 *     exactly as tall as the space under the header on mobile browsers whose
 *     100vh lies;
 *   * closing on Escape and on a click on the scrim;
 *   * reopening itself after a locale switch made from inside the drawer.
 */
if (!customElements.get('header-drawer')) {
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      if (!this.details || !this.summary) return;

      document.documentElement.classList.add('js');

      this.onToggle = this.toggle.bind(this);
      this.details.addEventListener('toggle', this.onToggle);

      this.onSummaryClick = (event) => {
        /* The scrim is drawn as summary::before, so a click on it lands on the
         * summary but outside the button box. Treat that as "close". */
        if (!this.details.open) return;
        const box = this.summary.getBoundingClientRect();
        const outside =
          event.clientY > box.bottom || event.clientX > box.right || event.clientX < box.left;
        if (outside) {
          event.preventDefault();
          this.close();
        }
      };
      this.summary.addEventListener('click', this.onSummaryClick);

      this.onKeydown = (event) => {
        if (event.key !== 'Escape' || !this.details.open) return;
        this.close();
        this.summary.focus();
      };
      document.addEventListener('keydown', this.onKeydown);

      this.onResize = this.measure.bind(this);
      window.addEventListener('resize', this.onResize);
      this.measure();

      this.restore();
    }

    disconnectedCallback() {
      if (this.details) this.details.removeEventListener('toggle', this.onToggle);
      if (this.summary) this.summary.removeEventListener('click', this.onSummaryClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
    }

    measure() {
      document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
      const header = this.closest('.shopify-section') || this.closest('header');
      if (header) {
        const bottom = header.getBoundingClientRect().bottom;
        document.documentElement.style.setProperty('--header-bottom-position', Math.max(bottom, 0) + 'px');
      }
    }

    toggle() {
      if (this.details.open) {
        this.measure();
        requestAnimationFrame(() => this.details.classList.add('menu-opening'));
        document.body.classList.add('overflow-hidden-tablet');
      } else {
        this.details.classList.remove('menu-opening');
        document.body.classList.remove('overflow-hidden-tablet');
      }
    }

    close() {
      this.details.classList.remove('menu-opening');
      document.body.classList.remove('overflow-hidden-tablet');
      setTimeout(() => {
        this.details.removeAttribute('open');
      }, 150);
    }

    /* A country switch made inside the drawer reloads the page. Reopen so the
     * visitor is not dropped back onto a closed menu. */
    restore() {
      let flagged = null;
      try {
        flagged = sessionStorage.getItem('menuWasOpen');
        if (flagged) sessionStorage.removeItem('menuWasOpen');
      } catch (error) {
        return;
      }
      if (flagged !== 'true') return;
      setTimeout(() => {
        this.details.open = true;
      }, 50);
    }
  }
  customElements.define('header-drawer', HeaderDrawer);
}

